import { useMemo, useState } from 'react'
import type { InvoicingStatus, TmsInvoiceRow } from '@numeo/types-main-web-app'
import { ActiveFactoringProviderLabels } from '@numeo/types-core'
import { Badge, Checkbox } from '@/components/ds2'
import { useFactoringStatusStore } from '@/hooks/factoringStatusStore'
import { useInvoicingRowsStore } from '../../invoicing/rowsStore'
import { useInvoicingChat } from '../../invoicing/chatBridge'
import { formatCount, formatUsd, loadsCount } from '../results/format'
import { InvoicingStatusBadge } from './InvoicingStatusBadge'
import { badgeLabelOf, filterMatches, rowKeyOf, sortedByStatus, sublineOf, statusOf, amountOf, TABLE_FILTERS, type TableFilter } from './invoicingRows'
import { pickPodFor } from './invoicingActions'
import { EMPTY_KEYS, useInvoicingSelectionStore } from './invoicingStores'
import { useInvoiceReview, type ReviewMode } from './review/reviewStore'
import { rateConMissingOf } from './review/reviewModel'

interface ReadyLoadsTableProps {
    messageId: string
    rows: TmsInvoiceRow[]
    /** The filter the table opens on (a suggestion's answer may open on Ready). */
    preset: TableFilter
    /** The whole count the card answered with; more than the rows shown means a note says so. */
    total: number
}

/** The actions a row offers, by its status: the review, the draft, the skip (Needs review only), the POD upload and the later marker. */
type RowAction = 'review' | 'draft' | 'skip' | 'upload'

const actionsOf = (status: InvoicingStatus, row: TmsInvoiceRow): RowAction[] => {
    switch (status) {
        case 'draft':
        case 'batch_prepared':
        case 'sent':
            return ['draft']
        case 'missing_pod':
            return ['review', 'upload']
        case 'needs_review':
            // Skip & submit creates the draft, which needs a rate confirmation: a load with none is reviewed, never skipped.
            return rateConMissingOf(row) ? ['review'] : ['review', 'skip']
        case 'ready':
        case 'other':
            return ['review']
    }
}

const ACTION_LABEL: Record<RowAction, string> = { review: 'Review', draft: 'View invoice', skip: 'Skip & submit', upload: 'Upload POD' }

/** The chat's invoicing table (v17 flows 2 to 4): status filters, selection that survives a filter change, and the row actions. */
export const ReadyLoadsTable = ({ messageId, rows, preset, total }: ReadyLoadsTableProps) => {
    const [filter, setFilter] = useState<TableFilter>(preset)
    const selected = useInvoicingSelectionStore((state) => state.byMessage[messageId] ?? EMPTY_KEYS)
    const setSelected = useInvoicingSelectionStore((state) => state.setSelected)
    const setAll = useInvoicingSelectionStore((state) => state.setAll)
    const chat = useInvoicingChat()
    const factoringProvider = useFactoringStatusStore((state) => state.activeProviders.main)
    const providerName = factoringProvider ? ActiveFactoringProviderLabels[factoringProvider] : undefined

    const visible = useMemo(() => sortedByStatus(rows).filter((row) => filterMatches(filter, statusOf(row))), [rows, filter])
    const visibleKeys = visible.map(rowKeyOf)
    const selectedVisible = visibleKeys.filter((key) => selected.includes(key)).length
    const allVisible = visible.length > 0 && selectedVisible === visible.length
    const sum = visible.reduce((acc, row) => acc + amountOf(row), 0)

    const openReview = (row: TmsInvoiceRow, mode: ReviewMode) => useInvoiceReview.getState().open(rowKeyOf(row), mode)

    const refreshRow = async (row: TmsInvoiceRow) => {
        const tabId = await chat.ensureTab()
        await useInvoicingRowsStore.getState().refresh(tabId, row.load_numbers)
    }

    const runAction = (action: RowAction, row: TmsInvoiceRow) => {
        if (action === 'review') openReview(row, 'review')
        else if (action === 'draft') openReview(row, 'draft')
        else if (action === 'skip') openReview(row, 'skip')
        else pickPodFor(row, () => void refreshRow(row))
    }

    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter loads">
                {TABLE_FILTERS.map((item) => (
                    <button
                        key={item.key}
                        type="button"
                        aria-pressed={filter === item.key}
                        onClick={() => setFilter(item.key)}
                        className={`rounded-md px-2.5 py-1 text-xs ${filter === item.key ? 'bg-accent-soft text-accent-soft-foreground' : 'bg-default text-foreground-muted hover:bg-default-hover'}`}
                    >
                        {item.label}
                    </button>
                ))}
            </div>

            <div className="overflow-x-auto rounded-xl border border-border">
                <table aria-label="Loads for invoicing" className="w-full text-xs">
                    <thead className="bg-surface-secondary text-foreground-muted">
                        <tr>
                            <th className="w-8 px-3 py-2">
                                <Checkbox
                                    size="sm"
                                    ariaLabel="Select all visible loads"
                                    checked={allVisible}
                                    indeterminate={selectedVisible > 0 && !allVisible}
                                    disabled={visible.length === 0}
                                    onChange={(on) => setAll(messageId, visibleKeys, on)}
                                />
                            </th>
                            <th className="px-3 py-2 text-left font-medium">Load / Customer</th>
                            <th className="px-3 py-2 text-left font-medium">Status / Documents</th>
                            <th className="px-3 py-2 text-right font-medium">Amount</th>
                            <th className="px-3 py-2" aria-label="Actions" />
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map((row) => {
                            const key = rowKeyOf(row)
                            const status = statusOf(row)
                            const number = row.load_numbers[0] ?? ''
                            const actions = actionsOf(status, row)
                            const main = actions.filter((action) => action === 'review' || action === 'draft')
                            const extra = actions.filter((action) => action !== 'review' && action !== 'draft')
                            return (
                                <tr key={key} className="border-t border-border align-top">
                                    <td className="px-3 py-2">
                                        <Checkbox
                                            size="sm"
                                            ariaLabel={`Select load ${number}`}
                                            checked={selected.includes(key)}
                                            onChange={(on) => setSelected(messageId, key, on)}
                                        />
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="font-medium text-foreground">#{number}</div>
                                        <div className="text-foreground-muted">{row.broker ?? '—'}</div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <InvoicingStatusBadge status={status} label={badgeLabelOf(row, status)} />
                                        <div className="mt-1 text-foreground-muted">{sublineOf(row, status, providerName)}</div>
                                        {row.driver_assigned === false && (
                                            <div className="mt-1" title={row.driver ? 'A driver is named but no truck is assigned' : 'No driver assigned'}>
                                                {/* No truck means no driver by the board's Unassigned rule; a load that still names a driver says only the truck is missing. */}
                                                <Badge variant="soft" type="warning" size="sm" label={row.driver ? 'No truck' : 'No driver'} />
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">{formatUsd(row.amount_due_usd)}</td>
                                    <td className="min-w-[115px] px-2 py-2">
                                        {/* v17: the soft Review / View invoice on the first line, the small text actions on one line under it. */}
                                        {main.map((action) => (
                                            <button
                                                key={action}
                                                type="button"
                                                className="whitespace-nowrap rounded-lg bg-accent-soft px-2 py-1 text-[11px] font-medium text-accent-soft-foreground hover:bg-accent-soft-hover"
                                                onClick={() => runAction(action, row)}
                                            >
                                                {ACTION_LABEL[action]}
                                            </button>
                                        ))}
                                        {extra.length > 0 && (
                                            <div className="mt-1 flex flex-nowrap gap-1">
                                                {extra.map((action) => (
                                                    <button
                                                        key={action}
                                                        type="button"
                                                        className="whitespace-nowrap rounded px-1 py-0.5 text-[10px] text-foreground-muted hover:bg-default hover:text-foreground"
                                                        onClick={() => runAction(action, row)}
                                                    >
                                                        {ACTION_LABEL[action]}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                    <tfoot className="bg-surface-secondary font-semibold">
                        <tr className="border-t border-border">
                            <td />
                            <td className="px-3 py-2" colSpan={2}>
                                {loadsCount(visible.length)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatUsd(sum)}</td>
                            <td />
                        </tr>
                    </tfoot>
                </table>
            </div>

            {visible.length === 0 && <p className="py-3 text-center text-xs text-foreground-muted">No loads in this status.</p>}
            {total > rows.length && (
                <p className="text-xs text-foreground-muted">
                    Showing {formatCount(rows.length)} of {loadsCount(total)}.
                </p>
            )}
        </div>
    )
}
